import { Item } from "../../pojo/Item";
import { ContactService } from "../../services/contact.service";
import { EventService } from "../../services/event.service";
import { FavoriteService } from "../../services/favorite.service";
import { NameService } from "../../services/name.service";
import { OrganizationService } from "../../services/organization.service";
import { ResourceService } from "../../services/resource.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { SpaceService } from "../../services/space.service";
import { S25Util } from "../s25-util";
import { ItemStore } from "./item.store";
import type { IdItem } from "./item.store.internal";

function defaultFavoriteLoader<T extends Item.Ids>(typeId: T): ItemStore.Loader<T> {
    return {
        handles: { favorite: true },
        fetch: (ids) =>
            Promise.all(
                ids.map((id) =>
                    FavoriteService.isFavorite({ itemTypeId: typeId, itemId: id }).then((resp) => ({
                        itemId: id,
                        itemTypeId: typeId,
                        favorite: !!resp?.isFavorite?.isFavorite,
                    })),
                ),
            ),
    };
}

function nameServiceHandler(typeId: Item.Ids): ItemStore.StoreHandler {
    return {
        handles: { name: true },
        handle: (item) => NameService.putItemToItemName(S25Util.toInt(item.itemId), typeId, item.itemName),
    };
}

const pageParams = ["cacheId", "pageNum", "pageSize", "filter", "filterFavorites", "domainFilter", "customFilterValue"];
export const EventItemStore = ItemStore.create({
    typeId: Item.Ids.Event,
    loaders: [
        { handles: { all: true }, fetch: (ids) => EventService.getSafeEventsMinimal(ids) },
        {
            handles: { name: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, pageParams),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getEvents(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Event)],
});
export type EventItemStore = InstanceType<typeof EventItemStore>;

export const OrganizationItemStore = ItemStore.create<Item.Ids.Organization>({
    typeId: Item.Ids.Organization,
    loaders: [
        {
            handles: { all: true },
            fetch: (ids, { loggedIn }) => {
                if (!loggedIn) {
                    throw new Error("Unauthorized to fetch organization names");
                }
                return OrganizationService.getOrganizationsMinimal(ids);
            },
        },
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, [...pageParams, "isIncludeQuantity"]),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getEventOrganizations(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Organization)],
});
export type OrganizationItemStore = InstanceType<typeof OrganizationItemStore>;

/** Paginated SE-flagged organizations for tile / multiselect (see {@link SearchCriteriaService.getStudentEngagementOrganizations}). */
export const StudentEngagementOrganizationsItemStore = ItemStore.create<Item.Ids.Organization>({
    typeId: Item.Ids.Organization,
    loaders: [
        {
            handles: { all: true },
            fetch: (ids, { loggedIn }) => {
                if (!loggedIn) {
                    throw new Error("Unauthorized to fetch organization names");
                }
                return OrganizationService.getOrganizationsMinimal(ids);
            },
        },
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, pageParams),
            fetchAll: async (): Promise<IdItem<Item.Ids.Organization>[]> =>
                (await SearchCriteriaService.fetchAllStudentEngagementOrganizationsEager()) as IdItem<Item.Ids.Organization>[],
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Organization)],
});
export type StudentEngagementOrganizationsItemStore = InstanceType<typeof StudentEngagementOrganizationsItemStore>;

export const ContactItemStore = ItemStore.create({
    typeId: Item.Ids.Contact,
    loaders: [
        { handles: { name: true, description: true }, fetch: (ids) => ContactService.getContactsNameAndFormal(ids) },
        defaultFavoriteLoader(Item.Ids.Contact),
        {
            handles: { name: true, description: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, pageParams),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getContacts(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Contact)],
});
export type ContactItemStore = InstanceType<typeof ContactItemStore>;

export const LocationItemStore = ItemStore.create({
    typeId: Item.Ids.Location,
    loaders: [
        { handles: { name: true, description: true }, fetch: (ids) => SpaceService.getSpacesNameAndFormal(ids) },
        defaultFavoriteLoader(Item.Ids.Location),
        {
            handles: { name: true, description: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, [...pageParams, "isIncludeQuantity"]),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getLocations(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Location)],
});
export type LocationItemStore = InstanceType<typeof LocationItemStore>;

export const ResourceItemStore = ItemStore.create({
    typeId: Item.Ids.Resource,
    loaders: [
        { handles: { name: true }, fetch: (ids) => ResourceService.getResourcesName(ids) },
        defaultFavoriteLoader(Item.Ids.Resource),
        {
            handles: { name: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, [...pageParams, "isIncludeQuantity"]),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getResources(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                    params?.isIncludeQuantity,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
    storeHandlers: [nameServiceHandler(Item.Ids.Resource)],
});
export type ResourceItemStore = InstanceType<typeof ResourceItemStore>;
